// src/pages/ChatCleanupPage.jsx - 채팅 메시지 정리 관리 페이지 (채팅 전용)

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trash2, 
  RotateCcw, 
  Info,
  Clock,
  FileText,
  Shield,
  PlayCircle,
  PauseCircle,
  MessageSquare,
  Users,
  BookOpen,
  MapPin,
  Building,
  Search,
  Filter,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart3,
  Database,
  Settings,
  RefreshCw
} from 'lucide-react';
import { 
  Card, 
  Button, 
  Table, 
  Tag, 
  Space, 
  Alert, 
  Modal, 
  Progress, 
  Statistic, 
  Row, 
  Col,
  Typography,
  Divider,
  Switch,
  Input,
  Select,
  Tabs,
  Badge,
  Tooltip,
  message as antMessage
} from 'antd';
import { db, appId } from '../firebase/firebaseConfig.jsx';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  getDocs, 
  deleteDoc, 
  doc,
  writeBatch,
  serverTimestamp,
  getCountFromServer,
  addDoc,
  setDoc,
  getDoc
} from 'firebase/firestore';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const ChatCleanupPage = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [cleanupHistory, setCleanupHistory] = useState([]);
  const [autoCleanupEnabled, setAutoCleanupEnabled] = useState(false);
  const [cleanupInterval, setCleanupInterval] = useState(24);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalData, setModalData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [chatServices, setChatServices] = useState([]);
  const [selectedService, setSelectedService] = useState('all');
  const [cutoffDays, setCutoffDays] = useState(30);
  const [chatRooms, setChatRooms] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newChatRoom, setNewChatRoom] = useState({
    id: '',
    name: '',
    description: '',
    type: 'industry',
    category: '',
    industry: '',
    location: ''
  });
  const [accurateStats, setAccurateStats] = useState(null);

  // 채팅 서비스 정의 - 실제 Firestore 컬렉션 기반
  const chatServiceTypes = [
    {
      id: 'chats',
      name: '소통의장 (일반 채팅)',
      icon: MessageSquare,
      color: 'blue',
      collections: [
        `artifacts/${appId}/chats`,
        `artifacts/${appId}/messages`
      ],
      description: '지역별, 업종별 소통의장 채팅방'
    },
    {
      id: 'groups',
      name: '그룹 채팅',
      icon: Users,
      color: 'green',
      collections: [
        'groups', 
        'groupMessages', 
        'groupMembers'
      ],
      description: '일반 그룹 채팅방'
    },
    {
      id: 'study_groups',
      name: '스터디 그룹 채팅',
      icon: BookOpen,
      color: 'purple',
      collections: [
        'koreanStudyGroups', 
        'koreanStudyGroupMessages', 
        'koreanStudyGroupMembers'
      ],
      description: '한국어 학습 그룹 채팅방'
    },
    {
      id: 'private_messages',
      name: '개인 메시지',
      icon: MessageSquare,
      color: 'orange',
      collections: [
        `artifacts/${appId}/privateMessages`
      ],
      description: '1:1 개인 메시지 (쪽지)'
    }
  ];

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    const initialize = async () => {
      await loadChatServices();
      await loadCleanupHistory();
      await loadChatRooms();
      await loadAccurateChatStats();
      // chatServices 로드 후 통계 로드
      await loadCleanupStats();
    };
    initialize();
  }, []);

  // chatServices 변경 시 통계 업데이트
  useEffect(() => {
    if (chatServices.length > 0) {
      loadCleanupStats();
    }
  }, [chatServices, cutoffDays]);

  /**
   * 채팅 서비스 목록 로드
   */
  const loadChatServices = async () => {
    try {
      setLoading(true);
      const services = [];

      for (const service of chatServiceTypes) {
        const serviceData = {
          ...service,
          stats: {
            totalRooms: 0,
            totalMessages: 0,
            oldMessages: 0,
            importantMessages: 0,
            deletableMessages: 0
          }
        };

        // 각 서비스별 통계 수집
        for (const collectionName of service.collections) {
          try {
            if (collectionName.includes('Messages') || collectionName.includes('messages') || collectionName.includes('privateMessages')) {
              // 메시지 컬렉션 통계
              const messagesRef = collection(db, collectionName);

              // 정확한 총 메시지 수: 서버 집계 사용
              try {
                const countAgg = await getCountFromServer(messagesRef);
                serviceData.stats.totalMessages += (countAgg.data().count || 0);
              } catch (e) {
                console.warn(`getCountFromServer failed for ${collectionName}:`, e);
              }

              // 삭제 대상/중요 메시지 통계: cutoffDate 이전 메시지 쿼리
              const cutoffDate = new Date();
              cutoffDate.setDate(cutoffDate.getDate() - cutoffDays);
              
              let oldMessages = 0;
              let importantMessages = 0;

              try {
                // 오래된 메시지 쿼리 (정확한 통계를 위해)
                const oldMessagesQuery = query(
                  messagesRef,
                  where('timestamp', '<', cutoffDate),
                  orderBy('timestamp', 'desc'),
                  limit(1000) // 샘플링을 위해 제한 (정확한 통계는 getCountFromServer 사용 고려)
                );
                
                const oldMessagesSnapshot = await getDocs(oldMessagesQuery);
                
                oldMessagesSnapshot.docs.forEach(doc => {
                  const data = doc.data();
                  if (!data || typeof data !== 'object') return;
                  
                  // 중요 메시지 확인
                  const content = (data.content || '').toLowerCase();
                  const isImportant = content.includes('레시피') || 
                                    content.includes('팁') || 
                                    content.includes('병원') || 
                                    content.includes('의료') ||
                                    content.includes('emergency') ||
                                    content.includes('urgent') ||
                                    content.includes('important');
                  
                  oldMessages++;
                  if (isImportant) {
                    importantMessages++;
                  }
                });

                // 샘플링된 결과를 기반으로 추정치 계산
                // 실제로는 getCountFromServer를 사용하여 정확한 수를 얻는 것이 좋지만,
                // where 조건과 함께 사용할 수 없으므로 샘플링 결과를 사용
                // 참고: 샘플링된 결과이므로 정확한 수치가 아닐 수 있음
                serviceData.stats.oldMessages += oldMessages;
                serviceData.stats.importantMessages += importantMessages;
                serviceData.stats.deletableMessages += Math.max(oldMessages - importantMessages, 0);
              } catch (queryError) {
                // 쿼리 실패 시 (인덱스 없음 등) 경고만 출력하고 계속 진행
                console.warn(`⚠️ 오래된 메시지 쿼리 실패 (${collectionName}):`, queryError);
                if (queryError.code === 'failed-precondition') {
                  console.warn(`인덱스가 필요합니다: ${collectionName} (timestamp < cutoffDate, orderBy timestamp)`);
                }
                // 쿼리 실패 시 통계는 0으로 유지
              }
      } else {
              // 채팅방/그룹 컬렉션 통계
              const roomsRef = collection(db, collectionName);
              const roomsSnapshot = await getDocs(roomsRef);
              serviceData.stats.totalRooms += roomsSnapshot.docs.length;
            }
          } catch (error) {
            console.warn(`Failed to load stats for ${collectionName}:`, error);
          }
        }

        services.push(serviceData);
      }

      setChatServices(services);
    } catch (error) {
      console.error('채팅 서비스 로드 실패:', error);
      antMessage.error('채팅 서비스 정보를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 정리 통계 로드
   */
  const loadCleanupStats = async () => {
    try {
      // chatServices가 로드되지 않았으면 대기
      if (!chatServices || chatServices.length === 0) {
        console.log('⏳ chatServices가 아직 로드되지 않음, 통계 로드 대기');
        return;
      }

      const totalStats = {
        totalRooms: 0,
        totalMessages: 0,
        oldMessages: 0,
        importantMessages: 0,
        deletableMessages: 0,
        cutoffDate: new Date(Date.now() - cutoffDays * 24 * 60 * 60 * 1000).toISOString()
      };

      chatServices.forEach(service => {
        if (service.stats) {
          totalStats.totalRooms += service.stats.totalRooms || 0;
          totalStats.totalMessages += service.stats.totalMessages || 0;
          totalStats.oldMessages += service.stats.oldMessages || 0;
          totalStats.importantMessages += service.stats.importantMessages || 0;
          totalStats.deletableMessages += service.stats.deletableMessages || 0;
        }
      });

      setStats(totalStats);
    } catch (error) {
      console.error('통계 로드 실패:', error);
    }
  };

  /**
   * 정리 이력 로드 - 실제 Firestore 데이터 기반
   */
  const loadCleanupHistory = async () => {
    try {
      setLoading(true);
      
      // 실제 Firestore에서 클린업 이력 가져오기
      // 여러 가능한 경로에서 시도
      const possiblePaths = [
        'adminCleanupHistory',  // 관리자 클린업 이력
        'systemLogs',           // 시스템 로그
        'adminLogs',            // 관리자 로그
        'cleanupLogs',          // 클린업 로그
        'auditTrail',           // 감사 추적
        `artifacts/${appId}/adminCleanupHistory`,  // 앱별 관리자 클린업 이력
        `artifacts/${appId}/systemLogs`,           // 앱별 시스템 로그
        `artifacts/${appId}/adminLogs`             // 앱별 관리자 로그
      ];

      let cleanupHistoryData = [];

      for (const collectionPath of possiblePaths) {
        try {
          console.log(`🔍 클린업 이력 경로 시도: ${collectionPath}`);
          const historyRef = collection(db, collectionPath);
          const historyQuery = query(
            historyRef, 
            where('type', '==', 'chat_cleanup'),  // 채팅 클린업 타입만
            orderBy('createdAt', 'desc'),
            limit(50)
          );
          
          const historySnapshot = await getDocs(historyQuery);
          console.log(`📊 ${collectionPath} - 클린업 이력 문서 수:`, historySnapshot.size);
          
          const pathData = historySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
            date: doc.data().createdAt?.toDate?.()?.toLocaleString() || new Date(doc.data().createdAt).toLocaleString(),
            _source: collectionPath
          }));
          
          cleanupHistoryData = [...cleanupHistoryData, ...pathData];
        } catch (pathError) {
          console.warn(`⚠️ ${collectionPath} 접근 실패:`, pathError.message);
        }
      }

      // 중복 제거 (같은 ID를 가진 문서가 여러 경로에 있을 수 있음)
      const uniqueHistory = cleanupHistoryData.reduce((acc, current) => {
        const existing = acc.find(item => item.id === current.id);
        if (!existing) {
          acc.push(current);
        }
        return acc;
      }, []);

      console.log('🔍 전체 로드된 클린업 이력:', uniqueHistory.length, '개');
      setCleanupHistory(uniqueHistory);

      // 실제 데이터가 없으면 빈 배열로 설정
      if (uniqueHistory.length === 0) {
        console.log('📝 클린업 이력이 없습니다. 실제 정리 작업을 수행하면 이력이 생성됩니다.');
        setCleanupHistory([]);
      }

    } catch (error) {
      console.error('❌ 클린업 이력 로드 실패:', error);
      antMessage.error('클린업 이력을 불러올 수 없습니다.');
      setCleanupHistory([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 채팅방 목록 로드
   */
  const loadChatRooms = async () => {
    try {
      setLoading(true);
      const chatRoomsRef = collection(db, `artifacts/${appId}/chats`);
      const chatRoomsQuery = query(chatRoomsRef, orderBy('createdAt', 'desc'));
      const chatRoomsSnapshot = await getDocs(chatRoomsQuery);
      
      const rooms = chatRoomsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date(doc.data().updatedAt)
      }));
      
      setChatRooms(rooms);
      console.log('✅ 채팅방 목록 로드 완료:', rooms.length, '개');
    } catch (error) {
      console.error('❌ 채팅방 목록 로드 실패:', error);
      antMessage.error('채팅방 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 실제 데이터 기반 정확한 통계 로드 (관리용 의미 있는 지표)
   * - 총 채팅방 수
   * - 업종별/지역별 채팅방 수
   * - 최근 7일 활성 채팅방 수 (메시지 존재)
   * - 최근 30일 휴면 채팅방 수 (메시지 없음)
   * - 참여자 없는 채팅방 수
   * - 총 메시지 수
   * - 최근 24시간 메시지 수
   */
  const loadAccurateChatStats = async () => {
    try {
      setLoading(true);
      const chatsRef = collection(db, `artifacts/${appId}/chats`);
      const messagesRef = collection(db, `artifacts/${appId}/messages`);

      // 기본 카운트 (서버 집계) - 에러 처리 포함
      let totalRoomsCount = 0;
      let totalMessagesCount = 0;
      let industryRoomsSnap = null;
      let locationRoomsSnap = null;
      let allRoomsSnap = null;

      try {
        const [totalRoomsAgg, totalMessagesAgg, industrySnap, locationSnap, allSnap] = await Promise.all([
          getCountFromServer(chatsRef).catch(e => {
            console.warn('채팅방 카운트 실패:', e);
            return { data: () => ({ count: 0 }) };
          }),
          getCountFromServer(messagesRef).catch(e => {
            console.warn('메시지 카운트 실패:', e);
            return { data: () => ({ count: 0 }) };
          }),
          getDocs(query(chatsRef, where('type', '==', 'industry'))).catch(e => {
            console.warn('업종별 채팅방 조회 실패:', e);
            return { docs: [] };
          }),
          getDocs(query(chatsRef, where('type', '==', 'location'))).catch(e => {
            console.warn('지역별 채팅방 조회 실패:', e);
            return { docs: [] };
          }),
          getDocs(chatsRef).catch(e => {
            console.warn('전체 채팅방 조회 실패:', e);
            return { docs: [] };
          })
        ]);

        totalRoomsCount = totalRoomsAgg.data().count || 0;
        totalMessagesCount = totalMessagesAgg.data().count || 0;
        industryRoomsSnap = industrySnap;
        locationRoomsSnap = locationSnap;
        allRoomsSnap = allSnap;
      } catch (error) {
        console.error('통계 기본 데이터 로드 실패:', error);
        // 기본값으로 계속 진행
      }

      // 프론트 기준: 사전 정의된 공식 채팅방만 통계에 포함
      const predefinedChatIds = [
        // 업종별
        'industry-chemical', 'industry-food', 'industry-metal', 'industry-machinery', 'industry-rubber', 'industry-other',
        // 지역별
        'location-yangon', 'location-mandalay', 'location-other'
      ];

      // allRoomsSnap이 null인 경우 처리
      const allRooms = allRoomsSnap && allRoomsSnap.docs ? allRoomsSnap.docs.map(d => ({ id: d.id, ...d.data() })) : [];
      const officialRooms = allRooms.filter(r => (r.isOfficial !== false) && predefinedChatIds.includes(r.id));
      const extraRooms = allRooms.filter(r => !predefinedChatIds.includes(r.id));

      const totalRooms = officialRooms.length; // 공식/정의된 방만 카운트
      const industryRooms = officialRooms.filter(r => r.type === 'industry').length;
      const locationRooms = officialRooms.filter(r => r.type === 'location').length;

      // 참여자 없는 방: 로컬 계산 (채팅방 스냅샷 기반)
      const emptyParticipantsRooms = officialRooms.reduce((acc, r) => {
        const participants = Array.isArray(r.participants) ? r.participants : [];
        return acc + (participants.length === 0 ? 1 : 0);
      }, 0);

      // 최근 24시간/7일/30일 기준 시간
      const now = new Date();
      const day24Ago = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const day7Ago = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const day30Ago = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // 최근 24시간 메시지 수 (서버 집계 쿼리)
      let recent24hMessages = 0;
      try {
        const recent24hQuery = query(messagesRef, where('timestamp', '>=', day24Ago));
        const recent24hAgg = await getCountFromServer(recent24hQuery);
        recent24hMessages = recent24hAgg.data().count || 0;
      } catch (error) {
        console.warn('최근 24시간 메시지 카운트 실패:', error);
        if (error.code === 'failed-precondition') {
          console.warn('인덱스가 필요합니다: messages (timestamp >= day24Ago)');
        }
      }

      // 최근 7일/30일 활성 방 계산: 최근 N 메시지를 가져와 roomId 기준으로 유니크 카운트
      // 주의: Firestore는 distinct count 미지원 → 합리적인 상한선으로 샘플링
      let activeRooms7d = 0;
      let activeRooms30d = 0;
      
      try {
        const recent7dQuery = query(messagesRef, where('timestamp', '>=', day7Ago), orderBy('timestamp', 'desc'), limit(5000));
        const recent30dQuery = query(messagesRef, where('timestamp', '>=', day30Ago), orderBy('timestamp', 'desc'), limit(5000));
        
        const [recent7dSnap, recent30dSnap] = await Promise.all([
          getDocs(recent7dQuery).catch(e => {
            console.warn('최근 7일 메시지 조회 실패:', e);
            return { docs: [] };
          }),
          getDocs(recent30dQuery).catch(e => {
            console.warn('최근 30일 메시지 조회 실패:', e);
            return { docs: [] };
          })
        ]);

        const activeRoomIds7d = new Set();
        recent7dSnap.docs.forEach(docSnap => {
          const data = docSnap.data() || {};
          // 다양한 필드명 시도: chatId, roomId, chatRoomId, room
          const rid = data.chatId || data.roomId || data.chatRoomId || data.room;
          if (rid) activeRoomIds7d.add(rid);
        });
        activeRooms7d = activeRoomIds7d.size;

        const activeRoomIds30d = new Set();
        recent30dSnap.docs.forEach(docSnap => {
          const data = docSnap.data() || {};
          // 다양한 필드명 시도: chatId, roomId, chatRoomId, room
          const rid = data.chatId || data.roomId || data.chatRoomId || data.room;
          if (rid) activeRoomIds30d.add(rid);
        });
        activeRooms30d = activeRoomIds30d.size;
      } catch (error) {
        console.warn('활성 방 계산 실패:', error);
        if (error.code === 'failed-precondition') {
          console.warn('인덱스가 필요합니다: messages (timestamp >= day7Ago/day30Ago, orderBy timestamp)');
        }
      }
      
      const dormantRooms30d = Math.max(totalRooms - activeRooms30d, 0);

      setAccurateStats({
        totalRooms,
        industryRooms,
        locationRooms,
        emptyParticipantsRooms,
        totalMessages: totalMessagesCount,
        recent24hMessages,
        activeRooms7d,
        dormantRooms30d,
        extraRooms: extraRooms.length
      });
    } catch (error) {
      console.error('정확 통계 로드 실패:', error);
      // 실패 시에도 UI가 동작하도록 null 유지
    } finally {
      setLoading(false);
    }
  };

  /**
   * 채팅방 생성
   */
  const createChatRoom = async () => {
    try {
      if (!newChatRoom.id || !newChatRoom.name) {
        antMessage.error('채팅방 ID와 이름을 입력해주세요.');
        return;
      }

      // ID 중복 확인
      const existingRoom = chatRooms.find(room => room.id === newChatRoom.id);
      if (existingRoom) {
        antMessage.error('이미 존재하는 채팅방 ID입니다.');
        return;
      }

      const chatRoomData = {
        ...newChatRoom,
        isOfficial: true,
        participants: [],
        lastMessage: null,
        lastMessageAt: null,
        unreadCount: {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const chatRef = doc(db, `artifacts/${appId}/chats`, newChatRoom.id);
      await setDoc(chatRef, chatRoomData);

      antMessage.success('채팅방이 성공적으로 생성되었습니다.');
      setShowCreateModal(false);
      setNewChatRoom({
        id: '',
        name: '',
        description: '',
        type: 'industry',
        category: '',
        industry: '',
        location: ''
      });
      
      // 채팅방 목록 새로고침
      await loadChatRooms();
    } catch (error) {
      console.error('❌ 채팅방 생성 실패:', error);
      antMessage.error('채팅방 생성에 실패했습니다.');
    }
  };

  /**
   * 채팅방 삭제
   */
  const deleteChatRoom = async (chatId) => {
    Modal.confirm({
      title: '채팅방 삭제',
      content: '이 채팅방을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.',
      okText: '삭제',
      cancelText: '취소',
      okType: 'danger',
      onOk: async () => {
        try {
          const chatRef = doc(db, `artifacts/${appId}/chats`, chatId);
          await deleteDoc(chatRef);
          
          antMessage.success('채팅방이 삭제되었습니다.');
          await loadChatRooms();
        } catch (error) {
          console.error('❌ 채팅방 삭제 실패:', error);
          antMessage.error('채팅방 삭제에 실패했습니다.');
        }
      }
    });
  };

  /**
   * 기본 채팅방들 일괄 생성
   */
  const createDefaultChatRooms = async () => {
    Modal.confirm({
      title: '기본 채팅방 생성',
      content: '모든 기본 채팅방(업종별, 지역별)을 생성하시겠습니까?',
      okText: '생성',
      cancelText: '취소',
      onOk: async () => {
        try {
          setLoading(true);
          
          const defaultChats = [
            // 업종별 채팅방
            {
              id: 'industry-chemical',
              type: 'industry',
              name: '🧪 화학업계 소통의장',
              description: '화학업계 종사자들을 위한 소통 공간입니다.',
              category: 'chemical',
              industry: 'Chemical',
              isOfficial: true
            },
            {
              id: 'industry-food',
              type: 'industry',
              name: '🍽️ 식품업계 소통의장',
              description: '식품업계 종사자들을 위한 소통 공간입니다.',
              category: 'food_beverage',
              industry: 'Food Beverage',
              isOfficial: true
            },
            {
              id: 'industry-metal',
              type: 'industry',
              name: '🔧 금속업계 소통의장',
              description: '금속업계 종사자들을 위한 소통 공간입니다.',
              category: 'metal_processing',
              industry: 'Metal Processing',
              isOfficial: true
            },
            {
              id: 'industry-machinery',
              type: 'industry',
              name: '⚙️ 기계업계 소통의장',
              description: '기계업계 종사자들을 위한 소통 공간입니다.',
              category: 'machinery',
              industry: 'Machinery Equipment',
              isOfficial: true
            },
            {
              id: 'industry-rubber',
              type: 'industry',
              name: '🔲 고무플라스틱업계 소통의장',
              description: '고무플라스틱업계 종사자들을 위한 소통 공간입니다.',
              category: 'rubber_plastic',
              industry: 'Rubber Plastic',
              isOfficial: true
            },
            {
              id: 'industry-other',
              type: 'industry',
              name: '🔧 기타 업계 소통의장',
              description: '기타 업계 종사자들을 위한 소통 공간입니다.',
              category: 'other',
              industry: 'Other',
              isOfficial: true
            },
            // 지역별 채팅방
            {
              id: 'location-yangon',
              type: 'location',
              name: '🏙️ 양곤 소통의장',
              description: '양곤 지역 종사자들을 위한 소통 공간입니다.',
              category: 'yangon',
              location: 'Yangon',
              isOfficial: true
            },
            {
              id: 'location-mandalay',
              type: 'location',
              name: '🏛️ 만달레이 소통의장',
              description: '만달레이 지역 종사자들을 위한 소통 공간입니다.',
              category: 'mandalay',
              location: 'Mandalay',
              isOfficial: true
            },
            {
              id: 'location-other',
              type: 'location',
              name: '🌍 기타 지역 소통의장',
              description: '기타 지역 종사자들을 위한 소통 공간입니다.',
              category: 'other',
              location: 'Other',
              isOfficial: true
            }
          ];

          let createdCount = 0;
          let skippedCount = 0;

          for (const chatData of defaultChats) {
            try {
              // 기존 채팅방 확인
              const existingRoom = chatRooms.find(room => room.id === chatData.id);
              if (existingRoom) {
                skippedCount++;
                continue;
              }

              const chatRoomData = {
                ...chatData,
                participants: [],
                lastMessage: null,
                lastMessageAt: null,
                unreadCount: {},
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              };

              const chatRef = doc(db, `artifacts/${appId}/chats`, chatData.id);
              await setDoc(chatRef, chatRoomData);
              createdCount++;
            } catch (error) {
              console.error(`❌ ${chatData.id} 생성 실패:`, error);
            }
          }

          antMessage.success(`기본 채팅방 생성 완료: ${createdCount}개 생성, ${skippedCount}개 건너뜀`);
          await loadChatRooms();
        } catch (error) {
          console.error('❌ 기본 채팅방 생성 실패:', error);
          antMessage.error('기본 채팅방 생성에 실패했습니다.');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  /**
   * 테스트 정리 실행
   */
  const runTestCleanup = async (serviceId = 'all') => {
    try {
      setLoading(true);
      setModalType('test');
      setModalVisible(true);
      
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - cutoffDays);
      
      let totalDeleted = 0;
      let totalSkipped = 0;
      let batchCount = 0;
      const serviceResults = [];

      const servicesToProcess = serviceId === 'all' ? chatServiceTypes : 
        chatServiceTypes.filter(s => s.id === serviceId);

      for (const service of servicesToProcess) {
        for (const collectionName of service.collections) {
          if (collectionName.includes('Messages') || collectionName === 'privateMessages') {
            try {
              const messagesRef = collection(db, collectionName);
              let messagesQuery;
              try {
                messagesQuery = query(
                  messagesRef, 
                  where('timestamp', '<', cutoffDate),
                  orderBy('timestamp', 'desc'),
                  limit(100) // 테스트용으로 제한
                );
              } catch (queryError) {
                // 쿼리 생성 실패 (인덱스 없음 등)
                console.warn(`⚠️ 쿼리 생성 실패 (${collectionName}):`, queryError);
                if (queryError.code === 'failed-precondition') {
                  console.warn(`인덱스가 필요합니다: ${collectionName}`);
                }
                continue; // 다음 컬렉션으로
              }
              
              const messagesSnapshot = await getDocs(messagesQuery);
              let serviceDeleted = 0;
              let serviceSkipped = 0;

              messagesSnapshot.docs.forEach(doc => {
                const data = doc.data();
                const content = (data.content || '').toLowerCase();
                const isImportant = content.includes('레시피') || 
                                  content.includes('팁') || 
                                  content.includes('병원') || 
                                  content.includes('의료') ||
                                  content.includes('emergency') ||
                                  content.includes('urgent') ||
                                  content.includes('important');
                
                if (isImportant) {
                  serviceSkipped++;
                } else {
                  serviceDeleted++;
                }
              });

              totalDeleted += serviceDeleted;
              totalSkipped += serviceSkipped;
              batchCount++;

              serviceResults.push({
                service: service.name,
                collection: collectionName,
                deleted: serviceDeleted,
                skipped: serviceSkipped
              });
            } catch (error) {
              console.warn(`Failed to process ${collectionName}:`, error);
            }
          }
        }
      }

      const result = {
          success: true,
        deleted: totalDeleted,
        skipped: totalSkipped,
        batchCount,
          dryRun: true,
        cutoffDate: cutoffDate.toISOString(),
        serviceResults,
        message: `테스트 완료: ${totalDeleted}개 삭제 예정, ${totalSkipped}개 보존`
        };
      
      setModalData(result);
      antMessage.success('테스트 정리가 완료되었습니다.');
      
    } catch (error) {
      console.error('테스트 정리 실패:', error);
      antMessage.error('테스트 정리에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 클린업 이력을 Firestore에 저장
   */
  const saveCleanupHistory = async (historyData) => {
    try {
      // 여러 가능한 경로에 저장 시도
      const possiblePaths = [
        'adminCleanupHistory',
        'systemLogs',
        `artifacts/${appId}/adminCleanupHistory`,
        `artifacts/${appId}/systemLogs`
      ];

      for (const collectionPath of possiblePaths) {
        try {
          const historyRef = collection(db, collectionPath);
          await addDoc(historyRef, historyData);
          console.log(`✅ 클린업 이력 저장 성공: ${collectionPath}`);
          break; // 하나 성공하면 중단
        } catch (error) {
          console.warn(`⚠️ ${collectionPath} 저장 실패:`, error.message);
        }
      }
    } catch (error) {
      console.error('❌ 클린업 이력 저장 실패:', error);
    }
  };

  /**
   * 실제 정리 실행
   */
  const runActualCleanup = async (serviceId = 'all') => {
    Modal.confirm({
      title: '채팅 메시지 정리 실행',
      content: (
        <div>
          <p>{cutoffDays}일 이상 된 채팅 메시지를 삭제합니다.</p>
          <p><strong>삭제 예정:</strong> {stats?.deletableMessages || 0}개</p>
          <p><strong>보존:</strong> {stats?.importantMessages || 0}개 (중요 메시지)</p>
          <Alert 
            message="이 작업은 되돌릴 수 없습니다!" 
            type="warning" 
            style={{ marginTop: 16 }}
          />
        </div>
      ),
      okText: '실행',
      cancelText: '취소',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true);
          setModalType('execute');
          setModalVisible(true);
          
          const startTime = Date.now(); // 시작 시간 기록
          const cutoffDate = new Date();
          cutoffDate.setDate(cutoffDate.getDate() - cutoffDays);
          
          let totalDeleted = 0;
          let totalSkipped = 0;
          let batchCount = 0;
          const serviceResults = [];

          const servicesToProcess = serviceId === 'all' ? chatServiceTypes : 
            chatServiceTypes.filter(s => s.id === serviceId);

          for (const service of servicesToProcess) {
            for (const collectionName of service.collections) {
              if (collectionName.includes('Messages') || collectionName.includes('privateMessages')) {
                try {
                  const messagesRef = collection(db, collectionName);
                  const messagesQuery = query(
                    messagesRef, 
                    where('timestamp', '<', cutoffDate),
                    orderBy('timestamp', 'desc')
                  );
                  
                  // 페이지네이션을 사용하여 모든 문서 처리
                  let lastDoc = null;
                  let hasMore = true;
                  let serviceDeleted = 0;
                  let serviceSkipped = 0;
                  let currentBatch = writeBatch(db);
                  let batchSize = 0;

                  while (hasMore) {
                    try {
                      // 페이지네이션 쿼리 구성
                      let paginatedQuery;
                      if (lastDoc) {
                        paginatedQuery = query(
                          messagesRef,
                          where('timestamp', '<', cutoffDate),
                          orderBy('timestamp', 'desc'),
                          startAfter(lastDoc),
                          limit(500) // 배치 크기와 동일하게 설정
                        );
                      } else {
                        paginatedQuery = query(
                          messagesRef,
                          where('timestamp', '<', cutoffDate),
                          orderBy('timestamp', 'desc'),
                          limit(500)
                        );
                      }

                      const messagesSnapshot = await getDocs(paginatedQuery);
                      
                      if (messagesSnapshot.empty) {
                        hasMore = false;
                        break;
                      }

                    // 문서 처리
                    for (const docSnap of messagesSnapshot.docs) {
                      const data = docSnap.data();
                      const content = (data.content || '').toLowerCase();
                      const isImportant = content.includes('레시피') || 
                                        content.includes('팁') || 
                                        content.includes('병원') || 
                                        content.includes('의료') ||
                                        content.includes('emergency') ||
                                        content.includes('urgent') ||
                                        content.includes('important');
                      
                      if (isImportant) {
                        serviceSkipped++;
                      } else {
                        currentBatch.delete(docSnap.ref);
                        serviceDeleted++;
                        batchSize++;
                        
                        // Firestore 배치 제한 (500개)
                        if (batchSize >= 500) {
                          await currentBatch.commit();
                          batchCount++;
                          batchSize = 0;
                          currentBatch = writeBatch(db); // 새로운 배치 생성
                        }
                      }
                      lastDoc = docSnap; // 마지막 문서 저장
                    }

                      // 더 이상 문서가 없으면 종료
                      if (messagesSnapshot.docs.length < 500) {
                        hasMore = false;
                      }
                    } catch (queryError) {
                      // 쿼리 에러 처리 (인덱스 없음 등)
                      console.error(`❌ 쿼리 실행 실패 (${collectionName}):`, queryError);
                      if (queryError.code === 'failed-precondition') {
                        antMessage.warning(`인덱스가 필요합니다: ${collectionName}. Firestore Console에서 인덱스를 생성해주세요.`);
                      }
                      hasMore = false;
                      break;
                    }
                  }

                  // 남은 배치 커밋
                  if (batchSize > 0) {
                    await currentBatch.commit();
                    batchCount++;
                  }

                  totalDeleted += serviceDeleted;
                  totalSkipped += serviceSkipped;

                  serviceResults.push({
                    service: service.name,
                    collection: collectionName,
                    deleted: serviceDeleted,
                    skipped: serviceSkipped
                  });
                } catch (error) {
                  console.warn(`Failed to process ${collectionName}:`, error);
                }
              }
            }
          }

          const result = {
              success: true,
            deleted: totalDeleted,
            skipped: totalSkipped,
            batchCount,
              dryRun: false,
            cutoffDate: cutoffDate.toISOString(),
            serviceResults,
            message: `정리 완료: ${totalDeleted}개 삭제, ${totalSkipped}개 보존`
          };
          
          // 실제 정리 이력을 Firestore에 저장
          await saveCleanupHistory({
            type: 'chat_cleanup',
            service: serviceId === 'all' ? 'all_services' : serviceId,
            deleted: totalDeleted,
            skipped: totalSkipped,
            batchCount,
            cutoffDate: cutoffDate.toISOString(),
            serviceResults,
            status: 'success',
            duration: `${Date.now() - startTime}ms`,
            adminId: 'admin@kairos.com', // 실제로는 현재 로그인한 관리자 ID
            createdAt: serverTimestamp()
          });
          
          setModalData(result);
          antMessage.success('채팅 메시지 정리가 완료되었습니다.');
          
          // 통계 새로고침
          await loadChatServices();
          await loadCleanupStats();
          await loadCleanupHistory();
          
        } catch (error) {
          console.error('정리 실행 실패:', error);
          antMessage.error('정리 실행에 실패했습니다.');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  /**
   * 자동 정리 토글
   */
  const toggleAutoCleanup = async (enabled) => {
    try {
      setAutoCleanupEnabled(enabled);
      antMessage.success(`자동 정리가 ${enabled ? '활성화' : '비활성화'}되었습니다.`);
    } catch (error) {
      console.error('자동 정리 설정 실패:', error);
      antMessage.error('자동 정리 설정에 실패했습니다.');
    }
  };

  /**
   * 실제 데이터 연결 테스트
   */
  const testDataConnection = async () => {
    try {
      setLoading(true);
      antMessage.info('실제 데이터 연결을 테스트하는 중...');
      
      const testResults = [];
      let totalCollections = 0;
      let accessibleCollections = 0;
      let totalDocuments = 0;
      
      for (const service of chatServiceTypes) {
        const serviceResult = {
          service: service.name,
          collections: [],
          summary: {
            total: 0,
            accessible: 0,
            documents: 0
          }
        };
        
        for (const collectionName of service.collections) {
          totalCollections++;
          serviceResult.summary.total++;
          
          try {
            const collectionRef = collection(db, collectionName);
            const testQuery = query(collectionRef, limit(10)); // 더 많은 문서 확인
            const snapshot = await getDocs(testQuery);
            
            const collectionInfo = {
              name: collectionName,
              accessible: true,
              documentCount: snapshot.size,
              hasData: snapshot.size > 0,
              sampleData: snapshot.docs.length > 0 ? {
                id: snapshot.docs[0].id,
                fields: Object.keys(snapshot.docs[0].data()),
                hasTimestamp: !!snapshot.docs[0].data().timestamp
              } : null
            };
            
            serviceResult.collections.push(collectionInfo);
            serviceResult.summary.accessible++;
            serviceResult.summary.documents += snapshot.size;
            accessibleCollections++;
            totalDocuments += snapshot.size;
            
            console.log(`✅ ${collectionName} 접근 성공: ${snapshot.size}개 문서`);
          } catch (error) {
            const collectionInfo = {
              name: collectionName,
              accessible: false,
              error: error.message,
              errorCode: error.code || 'UNKNOWN'
            };
            
            serviceResult.collections.push(collectionInfo);
            
            console.warn(`❌ ${collectionName} 접근 실패:`, error.message);
          }
        }
        
        testResults.push(serviceResult);
      }
      
      // 전체 요약 정보 추가
      const summary = {
        totalCollections,
        accessibleCollections,
        totalDocuments,
        successRate: totalCollections > 0 ? (accessibleCollections / totalCollections * 100).toFixed(1) : 0
      };
      
      // 결과 모달 표시
      setModalType('connection_test');
      setModalData({
        success: true,
        results: testResults,
        summary,
        message: `데이터 연결 테스트 완료 - ${summary.successRate}% 성공률`
      });
      setModalVisible(true);
      
      antMessage.success(`데이터 연결 테스트 완료: ${summary.successRate}% 성공률`);
      
    } catch (error) {
      console.error('데이터 연결 테스트 실패:', error);
      antMessage.error('데이터 연결 테스트에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 테이블 컬럼 정의 - 실제 Firestore 데이터 구조 기반
  const historyColumns = [
    {
      title: '실행 일시',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString();
      }
    },
    {
      title: '서비스',
      dataIndex: 'service',
      key: 'service',
      width: 120,
      render: (service) => {
        const serviceInfo = chatServiceTypes.find(s => s.id === service);
        const serviceName = service === 'all_services' ? 'All Services' : 
                           serviceInfo?.name || service;
        return (
          <Tag color={serviceInfo?.color || 'default'}>
            {serviceName}
          </Tag>
        );
      }
    },
    {
      title: '삭제된 메시지',
      dataIndex: 'deleted',
      key: 'deleted',
      width: 120,
      render: (value) => (
        <Tag color="red">{value || 0}개</Tag>
      )
    },
    {
      title: '보존된 메시지',
      dataIndex: 'skipped',
      key: 'skipped',
      width: 120,
      render: (value) => (
        <Tag color="green">{value || 0}개</Tag>
      )
    },
    {
      title: '소요 시간',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration) => {
        if (!duration) return '-';
        // duration이 "1234ms" 형태인 경우 초로 변환
        if (typeof duration === 'string' && duration.includes('ms')) {
          const ms = parseInt(duration.replace('ms', ''));
          return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
        }
        return duration;
      }
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'success' ? 'green' : 'red'}>
          {status === 'success' ? 'Success' : 'Failed'}
        </Tag>
      )
    },
    {
      title: '관리자',
      dataIndex: 'adminId',
      key: 'adminId',
      width: 120,
      render: (adminId) => (
        <Tag color="blue">{adminId || 'System'}</Tag>
      )
    }
  ];

  const serviceColumns = [
    {
      title: '서비스',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div className="flex items-center space-x-2">
          <record.icon className="w-4 h-4" />
          <span>{name}</span>
        </div>
      )
    },
    {
      title: '채팅방 수',
      dataIndex: ['stats', 'totalRooms'],
      key: 'totalRooms',
      render: (value) => <Tag color="blue">{value}개</Tag>
    },
    {
      title: '총 메시지',
      dataIndex: ['stats', 'totalMessages'],
      key: 'totalMessages',
      render: (value) => <Tag color="default">{value}개</Tag>
    },
    {
      title: '오래된 메시지',
      dataIndex: ['stats', 'oldMessages'],
      key: 'oldMessages',
      render: (value) => <Tag color="orange">{value}개</Tag>
    },
    {
      title: '삭제 가능',
      dataIndex: ['stats', 'deletableMessages'],
      key: 'deletableMessages',
      render: (value) => <Tag color="red">{value}개</Tag>
    },
    {
      title: '보존 예정',
      dataIndex: ['stats', 'importantMessages'],
      key: 'importantMessages',
      render: (value) => <Tag color="green">{value}개</Tag>
    },
    {
      title: '액션',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<Info />}
            onClick={() => runTestCleanup(record.id)}
            loading={loading}
          >
            테스트
          </Button>
          <Button
            size="small"
            danger
            icon={<Trash2 />}
            onClick={() => runActualCleanup(record.id)}
            loading={loading}
          >
            정리
          </Button>
        </Space>
      )
    }
  ];

  const chatRoomColumns = [
    {
      title: '채팅방 ID',
      dataIndex: 'id',
      key: 'id',
      width: 200,
      render: (id) => (
        <span className="font-mono text-sm text-blue-600">{id}</span>
      )
    },
    {
      title: '이름',
      dataIndex: 'name',
      key: 'name',
      render: (name) => (
        <span className="font-medium">{name}</span>
      )
    },
    {
      title: '타입',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => (
        <Tag color={type === 'industry' ? 'blue' : 'green'}>
          {type === 'industry' ? '업종별' : '지역별'}
        </Tag>
      )
    },
    {
      title: '카테고리',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category) => (
        <Tag color="default">{category}</Tag>
      )
    },
    {
      title: '참여자 수',
      dataIndex: 'participants',
      key: 'participants',
      width: 100,
      render: (participants) => (
        <Tag color="purple">{participants?.length || 0}명</Tag>
      )
    },
    {
      title: '생성일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => {
        if (!date) return '-';
        const d = date.toDate ? date.toDate() : new Date(date);
        return d.toLocaleDateString();
      }
    },
    {
      title: '액션',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            danger
            icon={<Trash2 />}
            onClick={() => deleteChatRoom(record.id)}
            loading={loading}
          >
            삭제
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.02%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      
      {/* Ultra Compact Government Chat Cleanup Dashboard Header */}
      <motion.div 
        initial={{ opacity: 0, y: -15, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative overflow-hidden bg-gradient-to-r from-yellow-500 via-red-500 to-green-500 rounded-lg shadow-lg border border-white/20 backdrop-blur-sm mb-6"
      >
        {/* Myanmar Flag Colors Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 via-red-500/20 to-green-500/20"></div>
        
        <div className="relative p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Ultra Compact Government Seal */}
              <div className="relative">
                <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full shadow-md border border-white/30 flex items-center justify-center">
                  <div className="w-6 h-6 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center">
                    <div className="w-4 h-4 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center">
                      <Trash2 className="w-3 h-3 text-white drop-shadow-lg" />
                    </div>
                  </div>
                </div>
      </div>

              <div className="text-white">
                <div className="flex items-center space-x-2">
                  <h1 className="text-lg font-bold drop-shadow-lg">
                    Chat Cleanup Management
                  </h1>
                  {/* Live Status Indicator */}
                  <div className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-200 border border-green-400/30">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                    <span>Live Monitoring</span>
                  </div>
                </div>
                <p className="text-xs text-white/80">Ministry of Labour - Advanced Chat Management Dashboard</p>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="flex items-center space-x-1 bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full">
                    <div className="w-1 h-1 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium">System Online</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Last Update */}
              <div className="text-right text-white">
                <div className="bg-white/10 backdrop-blur-sm rounded-md p-2 border border-white/20">
                  <div className="text-xs text-white/80">Last Update</div>
                  <div className="text-xs font-bold">{new Date().toLocaleString('en-US', { 
                    timeZone: 'Asia/Yangon',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</div>
                </div>
              </div>
              
              {/* Refresh Button */}
              <button
                onClick={() => {
              loadChatServices();
              loadCleanupStats();
              loadCleanupHistory();
              loadAccurateChatStats();
                }}
                disabled={loading}
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-lg p-2 transition-all duration-200 border border-white/20"
              >
                <RefreshCw className={`w-4 h-4 text-white ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="px-6 py-6">
        {/* Main Content - Professional Dashboard Style */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden"
        >
          {/* Compact 3D Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4 p-4">
            {/* Total Chat Messages - Compact 3D Card */}
            <motion.div
              whileHover={{ scale: 1.03, y: -3, rotateX: 5 }}
              className="relative overflow-hidden bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-700 rounded-xl shadow-2xl border border-white/20 backdrop-blur-sm transform perspective-1000"
              style={{ transformStyle: 'preserve-3d' }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-10 translate-x-10"></div>
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-8 -translate-x-8"></div>
              
              <div className="relative p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30 shadow-lg">
                    <MessageSquare className="w-5 h-5 text-white drop-shadow-lg" />
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/80 font-medium">Total Messages</div>
                    <div className="text-xs text-white/60">All Chat Services</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-2xl font-bold text-white drop-shadow-lg">
                    {stats?.totalMessages || 0}
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-white">Live</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Deletable Messages - Compact 3D Card */}
            <motion.div
              whileHover={{ scale: 1.03, y: -3, rotateX: 5 }}
              className="relative overflow-hidden bg-gradient-to-br from-red-500 via-pink-600 to-rose-700 rounded-xl shadow-2xl border border-white/20 backdrop-blur-sm transform perspective-1000"
              style={{ transformStyle: 'preserve-3d' }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-10 translate-x-10"></div>
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-8 -translate-x-8"></div>
              
              <div className="relative p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30 shadow-lg">
                    <Trash2 className="w-5 h-5 text-white drop-shadow-lg" />
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/80 font-medium">Deletable</div>
                    <div className="text-xs text-white/60">Old Messages</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-2xl font-bold text-white drop-shadow-lg">
                    {stats?.deletableMessages || 0}
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
                      <AlertTriangle className="w-3 h-3 text-white" />
                      <span className="text-xs font-medium text-white">Cleanup</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Protected Messages - Compact 3D Card */}
            <motion.div
              whileHover={{ scale: 1.03, y: -3, rotateX: 5 }}
              className="relative overflow-hidden bg-gradient-to-br from-green-500 via-emerald-600 to-teal-700 rounded-xl shadow-2xl border border-white/20 backdrop-blur-sm transform perspective-1000"
              style={{ transformStyle: 'preserve-3d' }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-10 translate-x-10"></div>
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-8 -translate-x-8"></div>
              
              <div className="relative p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30 shadow-lg">
                    <Shield className="w-5 h-5 text-white drop-shadow-lg" />
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/80 font-medium">Protected</div>
                    <div className="text-xs text-white/60">Important Messages</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-2xl font-bold text-white drop-shadow-lg">
                    {stats?.importantMessages || 0}
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3 text-white" />
                      <span className="text-xs font-medium text-white">Safe</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Chat Rooms - Compact 3D Card */}
            <motion.div
              whileHover={{ scale: 1.03, y: -3, rotateX: 5 }}
              className="relative overflow-hidden bg-gradient-to-br from-purple-500 via-violet-600 to-indigo-700 rounded-xl shadow-2xl border border-white/20 backdrop-blur-sm transform perspective-1000"
              style={{ transformStyle: 'preserve-3d' }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
              <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-10 translate-x-10"></div>
              <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-8 -translate-x-8"></div>
              
              <div className="relative p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30 shadow-lg">
                    <Users className="w-5 h-5 text-white drop-shadow-lg" />
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/80 font-medium">Chat Rooms</div>
                    <div className="text-xs text-white/60">Active Rooms</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-2xl font-bold text-white drop-shadow-lg">
                    {stats?.totalRooms || 0}
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1 bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-white">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Tabs for different views */}
          <div className="p-4 border-b border-white/20 bg-gradient-to-r from-white/5 to-white/10 backdrop-blur-sm">
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab}
              className="chat-cleanup-tabs"
              items={[
                {
                  key: 'overview',
                  label: (
                    <div className="flex items-center space-x-2">
                      <BarChart3 className="w-4 h-4" />
                      <span>Overview</span>
                    </div>
                  ),
                },
                {
                  key: 'services',
                  label: (
                    <div className="flex items-center space-x-2">
                      <Database className="w-4 h-4" />
                      <span>Chat Services</span>
                    </div>
                  ),
                },
                {
                  key: 'settings',
                  label: (
                    <div className="flex items-center space-x-2">
                      <Settings className="w-4 h-4" />
                      <span>Settings</span>
                    </div>
                  ),
                },
                {
                  key: 'chatrooms',
                  label: (
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="w-4 h-4" />
                      <span>Chat Rooms</span>
                    </div>
                  ),
                },
                {
                  key: 'history',
                  label: (
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4" />
                      <span>History</span>
                    </div>
                  ),
                }
              ]}
            />
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Auto Cleanup Settings */}
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/20 p-4">
                  <h3 className="text-white font-semibold text-sm mb-4 flex items-center space-x-2">
                    <Settings className="w-4 h-4" />
                    <span>Auto Cleanup Settings</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center space-x-3">
              <Switch
                checked={autoCleanupEnabled}
                onChange={toggleAutoCleanup}
                        checkedChildren={<PlayCircle className="w-3 h-3" />}
                        unCheckedChildren={<PauseCircle className="w-3 h-3" />}
                      />
                      <span className="text-white text-sm">
                        Auto Cleanup {autoCleanupEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white/80 text-sm">Interval:</span>
              <Input
                type="number"
                value={cleanupInterval}
                onChange={(e) => setCleanupInterval(Number(e.target.value))}
                        addonAfter="hours"
                        className="w-24"
                disabled={!autoCleanupEnabled}
              />
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white/80 text-sm">Cutoff:</span>
                      <Input
                        type="number"
                        value={cutoffDays}
                        onChange={(e) => setCutoffDays(Number(e.target.value))}
                        addonAfter="days"
                        className="w-24"
                      />
                    </div>
                  </div>
                </div>

                {/* Manual Cleanup Actions */}
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/20 p-4">
                  <h3 className="text-white font-semibold text-sm mb-4 flex items-center space-x-2">
                    <Trash2 className="w-4 h-4" />
                    <span>Manual Cleanup Actions</span>
                  </h3>
                  <div className="flex flex-wrap gap-3">
          <Button
            type="primary"
            icon={<Info />}
                      onClick={() => runTestCleanup()}
            loading={loading}
                      className="bg-blue-500 hover:bg-blue-600 border-blue-500"
          >
                      Test Cleanup (All Services)
          </Button>
          <Button
            type="primary"
            danger
            icon={<Trash2 />}
                      onClick={() => runActualCleanup()}
            loading={loading}
                      className="bg-red-500 hover:bg-red-600 border-red-500"
          >
                      Execute Cleanup (All Services)
          </Button>
          <Button
                      icon={<Database />}
                      onClick={testDataConnection}
            loading={loading}
                      className="bg-green-500 hover:bg-green-600 border-green-500 text-white"
          >
                      Test Data Connection
          </Button>
                    <Button
                      icon={<RefreshCw />}
                      onClick={() => {
                        loadChatServices();
                        loadCleanupStats();
                        loadCleanupHistory();
                        loadAccurateChatStats();
                      }}
                      loading={loading}
                      className="bg-white/10 hover:bg-white/20 border-white/20 text-white"
                    >
                      Refresh Data
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'services' && (
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/20 overflow-hidden">
                <div className="p-4 border-b border-white/20">
                  <h3 className="text-white font-semibold text-sm flex items-center space-x-2">
                    <Database className="w-4 h-4" />
                    <span>Chat Services Management</span>
                  </h3>
                </div>
                <div className="p-4">
                  <Table
                    columns={serviceColumns}
                    dataSource={chatServices}
                    rowKey="id"
                    pagination={false}
                    loading={loading}
                    className="chat-services-table"
                  />
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/20 p-4">
                  <h3 className="text-white font-semibold text-sm mb-4 flex items-center space-x-2">
                    <Settings className="w-4 h-4" />
                    <span>Cleanup Configuration</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/80 text-sm mb-2">Cutoff Period (Days)</label>
                      <Input
                        type="number"
                        value={cutoffDays}
                        onChange={(e) => setCutoffDays(Number(e.target.value))}
                        addonAfter="days"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm mb-2">Auto Cleanup Interval</label>
                      <Input
                        type="number"
                        value={cleanupInterval}
                        onChange={(e) => setCleanupInterval(Number(e.target.value))}
                        addonAfter="hours"
                        className="w-full"
                        disabled={!autoCleanupEnabled}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/20 p-4">
                  <h3 className="text-white font-semibold text-sm mb-4 flex items-center space-x-2">
                    <Shield className="w-4 h-4" />
                    <span>Message Protection Rules</span>
                  </h3>
                  <div className="space-y-3">
                    <div className="text-white/80 text-sm">
                      <strong>Important Message Keywords:</strong>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['레시피', '팁', '병원', '의료', 'emergency', 'urgent', 'important'].map(keyword => (
                        <Tag key={keyword} color="green" className="text-xs">
                          {keyword}
                        </Tag>
                      ))}
                    </div>
                    <Alert
                      message="Protection Notice"
                      description="Messages containing these keywords will be automatically preserved during cleanup."
                      type="info"
                      showIcon
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'chatrooms' && (
              <div className="space-y-6">
                {/* 채팅방 관리 헤더 */}
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/20 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold text-sm flex items-center space-x-2">
                      <MessageSquare className="w-4 h-4" />
                      <span>Chat Room Management</span>
                    </h3>
                    <div className="flex space-x-2">
                      <Button
                        type="primary"
                        icon={<MessageSquare />}
                        onClick={() => setShowCreateModal(true)}
                        className="bg-blue-500 hover:bg-blue-600 border-blue-500"
                      >
                        새 채팅방 생성
                      </Button>
                      <Button
                        icon={<RefreshCw />}
                        onClick={createDefaultChatRooms}
                        loading={loading}
                        className="bg-green-500 hover:bg-green-600 border-green-500 text-white"
                      >
                        기본 채팅방 생성
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                      <div className="text-white/80 text-xs mb-1">총 채팅방</div>
                      <div className="text-white text-lg font-bold">{accurateStats?.totalRooms ?? chatRooms.length}개</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                      <div className="text-white/80 text-xs mb-1">업종별</div>
                      <div className="text-white text-lg font-bold">{accurateStats?.industryRooms ?? chatRooms.filter(room => room.type === 'industry').length}개</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                      <div className="text-white/80 text-xs mb-1">지역별</div>
                      <div className="text-white text-lg font-bold">{accurateStats?.locationRooms ?? chatRooms.filter(room => room.type === 'location').length}개</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                      <div className="text-white/80 text-xs mb-1">참여자 없는 방</div>
                      <div className="text-white text-lg font-bold">{accurateStats?.emptyParticipantsRooms ?? 0}개</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                      <div className="text-white/80 text-xs mb-1">최근 24시간 메시지</div>
                      <div className="text-white text-lg font-bold">{accurateStats?.recent24hMessages ?? 0}개</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                      <div className="text-white/80 text-xs mb-1">최근 7일 활성 방</div>
                      <div className="text-white text-lg font-bold">{accurateStats?.activeRooms7d ?? 0}개</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                      <div className="text-white/80 text-xs mb-1">최근 30일 휴면 방</div>
                      <div className="text-white text-lg font-bold">{accurateStats?.dormantRooms30d ?? 0}개</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                      <div className="text-white/80 text-xs mb-1">총 메시지</div>
                      <div className="text-white text-lg font-bold">{accurateStats?.totalMessages ?? 0}개</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                      <div className="text-white/80 text-xs mb-1">비표준/추가 방</div>
                      <div className="text-white text-lg font-bold">{accurateStats?.extraRooms ?? 0}개</div>
                    </div>
                  </div>
                </div>

                {/* 채팅방 목록 테이블 */}
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/20 overflow-hidden">
                  <div className="p-4 border-b border-white/20">
                    <h3 className="text-white font-semibold text-sm flex items-center space-x-2">
                      <Database className="w-4 h-4" />
                      <span>Chat Rooms List</span>
                    </h3>
                  </div>
                  <div className="p-4">
                    <Table
                      columns={chatRoomColumns}
                      dataSource={chatRooms}
                      rowKey="id"
                      pagination={{ pageSize: 10 }}
                      loading={loading}
                      className="chat-rooms-table"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/20 overflow-hidden">
                <div className="p-4 border-b border-white/20">
                  <h3 className="text-white font-semibold text-sm flex items-center space-x-2">
                    <Clock className="w-4 h-4" />
                    <span>Cleanup History</span>
                  </h3>
                </div>
                <div className="p-4">
                  {cleanupHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-white/60 mb-4">
                        <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <h3 className="text-lg font-medium text-white/80 mb-2">No Cleanup History</h3>
                        <p className="text-white/60">
                          No chat cleanup operations have been performed yet.
                        </p>
                        <p className="text-white/60 text-sm mt-2">
                          Perform a cleanup operation to see the history here.
                        </p>
                      </div>
                    </div>
                  ) : (
        <Table
          columns={historyColumns}
          dataSource={cleanupHistory}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          loading={loading}
                      className="cleanup-history-table"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Result Modal */}
      <Modal
        title={
          modalType === 'test' ? 'Test Cleanup Results' :
          modalType === 'execute' ? 'Cleanup Execution Results' :
          modalType === 'connection_test' ? 'Data Connection Test Results' : 'Details'
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
        className="cleanup-result-modal"
      >
        {modalData && (
          <div>
            {modalType === 'connection_test' ? (
              // 연결 테스트 결과
              <div>
                <h3 className="text-lg font-semibold mb-4">Data Connection Test Results</h3>
                
                {/* 전체 요약 */}
                {modalData.summary && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">전체 요약</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">총 컬렉션:</span>
                        <span className="font-semibold ml-2">{modalData.summary.totalCollections}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">접근 가능:</span>
                        <span className="font-semibold ml-2 text-green-600">{modalData.summary.accessibleCollections}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">총 문서:</span>
                        <span className="font-semibold ml-2">{modalData.summary.totalDocuments}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">성공률:</span>
                        <span className="font-semibold ml-2 text-blue-600">{modalData.summary.successRate}%</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {modalData.results.map((service, index) => (
                  <div key={index} className="mb-6 p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-blue-600">{service.service}</h4>
                      <div className="text-sm text-gray-600">
                        {service.summary.accessible}/{service.summary.total} 컬렉션 접근 가능
                      </div>
                    </div>
                    <div className="space-y-2">
                      {service.collections.map((collection, colIndex) => (
                        <div key={colIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                          <div className="flex items-center space-x-3">
                            <span className="font-mono text-sm">{collection.name}</span>
                            {collection.accessible ? (
                              <Tag color="green">Accessible</Tag>
                            ) : (
                              <Tag color="red">Failed</Tag>
                            )}
                            {collection.hasData && (
                              <Tag color="blue">Has Data</Tag>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">
                            {collection.accessible ? (
                              <div className="text-right">
                                <div>{collection.documentCount} documents</div>
                                {collection.sampleData && (
                                  <div className="text-xs text-gray-500">
                                    Fields: {collection.sampleData.fields.join(', ')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-right text-red-500">
                                <div>{collection.error}</div>
                                {collection.errorCode && (
                                  <div className="text-xs">Code: {collection.errorCode}</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // 정리 결과
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Deleted Messages"
                    value={modalData.deleted}
                    suffix="messages"
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Preserved Messages"
                    value={modalData.skipped}
                    suffix="messages"
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
              </Row>
            )}
            
            {modalType !== 'connection_test' && (
              <>
                <Divider />
                
                <div>
                  <Text strong>Cutoff Date: </Text>
                  <Text>{new Date(modalData.cutoffDate).toLocaleString()}</Text>
                </div>
                
                <div style={{ marginTop: 8 }}>
                  <Text strong>Batch Processing: </Text>
                  <Text>{modalData.batchCount} batches</Text>
                </div>
                
                <div style={{ marginTop: 8 }}>
                  <Text strong>Mode: </Text>
                  <Tag color={modalData.dryRun ? 'blue' : 'red'}>
                    {modalData.dryRun ? 'Test Run' : 'Actual Execution'}
                  </Tag>
                </div>

                {modalData.serviceResults && (
                  <div style={{ marginTop: 16 }}>
                    <Text strong>Service Results:</Text>
                    <div className="mt-2 space-y-2">
                      {modalData.serviceResults.map((result, index) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="text-sm">{result.service} - {result.collection}</span>
                          <div className="flex space-x-2">
                            <Tag color="red">{result.deleted} deleted</Tag>
                            <Tag color="green">{result.skipped} preserved</Tag>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <Alert
                  message={modalData.message}
                  type={modalData.success ? 'success' : 'error'}
                  style={{ marginTop: 16 }}
                />
              </>
            )}
          </div>
        )}
      </Modal>

      {/* 채팅방 생성 모달 */}
      <Modal
        title="새 채팅방 생성"
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onOk={createChatRoom}
        okText="생성"
        cancelText="취소"
        width={600}
        className="chat-room-create-modal"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              채팅방 ID *
            </label>
            <Input
              value={newChatRoom.id}
              onChange={(e) => setNewChatRoom(prev => ({ ...prev, id: e.target.value }))}
              placeholder="예: industry-new, location-seoul"
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              채팅방 이름 *
            </label>
            <Input
              value={newChatRoom.name}
              onChange={(e) => setNewChatRoom(prev => ({ ...prev, name: e.target.value }))}
              placeholder="예: 🏭 신규업계 소통의장"
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              설명
            </label>
            <TextArea
              value={newChatRoom.description}
              onChange={(e) => setNewChatRoom(prev => ({ ...prev, description: e.target.value }))}
              placeholder="채팅방에 대한 설명을 입력하세요"
              rows={3}
              className="w-full"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                타입
              </label>
              <Select
                value={newChatRoom.type}
                onChange={(value) => setNewChatRoom(prev => ({ ...prev, type: value }))}
                className="w-full"
              >
                <Select.Option value="industry">업종별</Select.Option>
                <Select.Option value="location">지역별</Select.Option>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                카테고리
              </label>
              <Input
                value={newChatRoom.category}
                onChange={(e) => setNewChatRoom(prev => ({ ...prev, category: e.target.value }))}
                placeholder="예: chemical, yangon"
                className="w-full"
              />
            </div>
          </div>
          
          {newChatRoom.type === 'industry' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                업종
              </label>
              <Input
                value={newChatRoom.industry}
                onChange={(e) => setNewChatRoom(prev => ({ ...prev, industry: e.target.value }))}
                placeholder="예: Chemical, Food Beverage"
                className="w-full"
              />
            </div>
          )}
          
          {newChatRoom.type === 'location' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                지역
              </label>
              <Input
                value={newChatRoom.location}
                onChange={(e) => setNewChatRoom(prev => ({ ...prev, location: e.target.value }))}
                placeholder="예: Yangon, Mandalay"
                className="w-full"
              />
            </div>
          )}
        </div>
      </Modal>

      <style jsx>{`
        .chat-cleanup-tabs .ant-tabs-tab {
          color: rgba(255, 255, 255, 0.8) !important;
        }
        .chat-cleanup-tabs .ant-tabs-tab-active {
          color: white !important;
        }
        .chat-cleanup-tabs .ant-tabs-ink-bar {
          background: white !important;
        }
        .chat-services-table .ant-table-thead > tr > th {
          background: rgba(255, 255, 255, 0.1) !important;
          color: white !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
        }
        .chat-services-table .ant-table-tbody > tr > td {
          background: rgba(255, 255, 255, 0.05) !important;
          color: white !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
        }
        .chat-services-table .ant-table-tbody > tr:hover > td {
          background: rgba(255, 255, 255, 0.1) !important;
        }
        .cleanup-history-table .ant-table-thead > tr > th {
          background: rgba(255, 255, 255, 0.1) !important;
          color: white !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
        }
        .cleanup-history-table .ant-table-tbody > tr > td {
          background: rgba(255, 255, 255, 0.05) !important;
          color: white !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
        }
        .cleanup-history-table .ant-table-tbody > tr:hover > td {
          background: rgba(255, 255, 255, 0.1) !important;
        }
        .cleanup-result-modal .ant-modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
        }
        .cleanup-result-modal .ant-modal-title {
          color: white !important;
        }
        .cleanup-result-modal .ant-modal-close {
          color: white !important;
        }
        .cleanup-result-modal .ant-modal-body {
          background: #f8fafc !important;
        }
        .chat-rooms-table .ant-table-thead > tr > th {
          background: rgba(255, 255, 255, 0.1) !important;
          color: white !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
        }
        .chat-rooms-table .ant-table-tbody > tr > td {
          background: rgba(255, 255, 255, 0.05) !important;
          color: white !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
        }
        .chat-rooms-table .ant-table-tbody > tr:hover > td {
          background: rgba(255, 255, 255, 0.1) !important;
        }
        .chat-room-create-modal .ant-modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
        }
        .chat-room-create-modal .ant-modal-title {
          color: white !important;
        }
        .chat-room-create-modal .ant-modal-close {
          color: white !important;
        }
        .chat-room-create-modal .ant-modal-body {
          background: #f8fafc !important;
        }
      `}</style>
    </div>
  );
};

export default ChatCleanupPage;